import { useEffect, useState } from "react";
import type { DashboardData } from "../types";
import { api, ApiError } from "../api";
import ErrorState from "./ErrorState";
import { LineChart, BarChart, PieChart } from "./charts";
import {
  IconAlert,
  IconCheckCircle,
  IconRepeat,
  IconClock,
  IconClipboardCheck,
  IconSmile,
  IconCertificate,
  IconWrench,
  IconLayers,
} from "../icons";

const COLORS = ["#0c7a3f", "#15a85a", "#34d27e", "#0a5c30", "#5cd99a", "#1f7a4d", "#8be0b3", "#0e9488"];

export default function Dashboard({ refreshKey }: { refreshKey: number }) {
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let alive = true;
    setError(null);
    api
      .dashboard()
      .then((d) => alive && setData(d))
      .catch((err) => {
        if (!alive) return;
        setError(err instanceof ApiError ? err : new ApiError(String(err)));
      });
    return () => {
      alive = false;
    };
  }, [refreshKey, reload]);

  if (error)
    return (
      <ErrorState
        message={error.message}
        code={error.code}
        onRetry={() => setReload((n) => n + 1)}
      />
    );
  if (!data) return <div className="empty">Carregando indicadores...</div>;

  const c = data.cards;

  /**
   * De onde saiu a nota de satisfação. O indicador soma as pesquisas de cliente
   * lançadas aqui com a avaliação de cada serviço executado, que vem da Gestão
   * da Operação — antes ele olhava só as pesquisas daqui e ficava em zero mesmo
   * com a operação toda avaliada.
   */
  const origemSatisfacao = (): string => {
    if (c.satisfacao_integracao === "token") {
      return "só pesquisas daqui — a Gestão da Operação recusou a integração (token diferente)";
    }
    if (c.satisfacao_integracao === "offline") {
      return "só pesquisas daqui — a Gestão da Operação não respondeu";
    }
    if (c.satisfacao_total_avaliacoes === 0) {
      return "nenhuma avaliação lançada ainda (nem pesquisa aqui, nem serviço na Operação)";
    }
    const partes: string[] = [];
    if (c.satisfacao_pesquisas > 0) partes.push(`${c.satisfacao_pesquisas} pesquisa(s) de cliente`);
    if (c.satisfacao_execucao_avaliacoes > 0) {
      partes.push(`${c.satisfacao_execucao_avaliacoes} serviço(s) avaliado(s) na Operação`);
    }
    return partes.join(" + ");
  };

  const cards = [
    { label: "NCs abertas no mês", value: c.ncs_abertas_mes, Icon: IconAlert, tone: "amber" },
    { label: "NCs em aberto", value: c.ncs_em_aberto, Icon: IconClock, tone: "red" },
    { label: "NCs encerradas", value: c.ncs_encerradas, Icon: IconCheckCircle, tone: "green" },
    { label: "NCs reincidentes", value: c.ncs_reincidentes, Icon: IconRepeat, tone: "red" },
    { label: "Total de NCs", value: c.ncs_total, Icon: IconLayers, tone: "teal" },
    { label: "Ações pendentes", value: c.acoes_pendentes, Icon: IconWrench, tone: "amber" },
    { label: "Auditorias sem ressalvas", value: `${c.auditorias_sem_ressalvas}%`, Icon: IconClipboardCheck, tone: "emerald" },
    {
      label: "Satisfação do cliente",
      value: c.satisfacao_total_avaliacoes > 0 ? c.satisfacao_media : "—",
      Icon: IconSmile,
      tone: "lime",
      detalhe: origemSatisfacao(),
    },
    { label: "Conformidade ISO/ABNT/IEC", value: `${c.conformidade_media}%`, Icon: IconCertificate, tone: "green" },
    { label: "Taxa de retrabalho", value: `${c.taxa_retrabalho}%`, Icon: IconRepeat, tone: "red" },
  ];

  return (
    <div className="dashboard">
      <div className="cards">
        {cards.map((card) => (
          <div
            className={`card tone-${card.tone}`}
            key={card.label}
            title={"detalhe" in card ? String(card.detalhe) : undefined}
          >
            <div className="card-icon">
              <card.Icon size={22} />
            </div>
            <div className="card-body">
              <span className="card-value">{card.value}</span>
              <span className="card-label">{card.label}</span>
              {"detalhe" in card && <span className="card-origem">{String(card.detalhe)}</span>}
            </div>
          </div>
        ))}
      </div>

      <div className="charts">
        <ChartBox title="NCs abertas vs. encerradas (12 meses)">
          <LineChart
            data={data.ncsPorMes}
            xKey="mes"
            series={[
              { key: "abertas", label: "Abertas", color: "#dc2626" },
              { key: "encerradas", label: "Encerradas", color: "#0c7a3f" },
            ]}
          />
        </ChartBox>

        <ChartBox title="NCs por departamento">
          <PieChart
            data={data.ncsPorDepto}
            valueKey="total"
            labelKey="departamento"
            colors={COLORS}
          />
        </ChartBox>

        <ChartBox title="Auditorias por departamento">
          <BarChart
            data={data.auditoriasPorDepto}
            xKey="departamento"
            series={[{ key: "total", label: "Auditorias", color: "#0c7a3f" }]}
          />
        </ChartBox>

        <ChartBox title="Conformidade por norma (ISO / ABNT / IEC)">
          <BarChart
            data={data.conformidadePorNorma}
            xKey="norma"
            max={100}
            series={[{ key: "media", label: "Conformidade média (%)", color: "#15a85a" }]}
          />
        </ChartBox>
      </div>
    </div>
  );
}

function ChartBox({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="chart-box">
      <h3>{title}</h3>
      {children}
    </div>
  );
}
